<template>
  <v-card class="elevation-2 ma-2">
    <v-card-text class="pt-0">
      <div class="text-overline font-weight-bold">EBS</div>

      <v-divider class="py-1 mx-2" style="border-color: #607d8b"></v-divider>

      <div class="ml-1 mb-2 d-flex flex-column">
        <span class="mb-1">
          <v-icon :class="checkCANBUS" x-small>mdi-circle</v-icon>
          {{ computedCanbusTimestamp }}
        </span>
      </div>

      <span class="mb-1" :class="isPinaOn ? 'font-weight-bold' : ''">
        <v-chip label x-small class="px-1 py-3" :color="isPinaOn ? 'rgb(33, 150, 243, 0.7)' : ''">
          <v-icon small>mdi-truck-flatbed</v-icon>
        </v-chip>
        {{ $t('device.last_frames.can_bus.tractor_unit') }}
      </span>

      <!-- RUEDAS -->
      <v-row v-if="device?.last_frames?.frame_CAN?.payloadCAN" class="pt-4">
        <v-col>
          <v-row v-for="(row, index) in getTyresRows" :key="index" dense no-gutters class="align-center mb-2">
            <v-col cols="12">
              <div class="tyre-line">
                <!-- IZQUIERDA -->
                <div class="side">
                  <v-chip x-small class="white--text blue-grey lighten-1 mr-1 tyre-chip">
                    {{ $t('device.last_frames.can_bus.tyres', { tyre: row.left.at(-1) }) }}
                  </v-chip>

                  <!-- TOOLTIP  -->
                  <v-tooltip v-if="hasPressureAlarm(row.left) || hasTempAlarm(row.left)" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        v-bind="attrs"
                        v-on="on"
                        x-small
                        class="blue-grey lighten-4 data-chip"
                        :class="{ 'alarm-chip': hasPressureAlarm(row.left) || hasTempAlarm(row.left) }">
                        {{ getTyreLabel(row.left) }}
                      </v-chip>
                    </template>
                    <span>{{ getAlarmTooltip(row.left) }}</span>
                  </v-tooltip>

                  <v-chip v-else x-small class="blue-grey lighten-4 data-chip">
                    {{ getTyreLabel(row.left) }}
                  </v-chip>
                </div>

                <v-divider vertical class="mx-2" />

                <!-- DERECHA -->
                <div class="side" v-if="row.right">
                  <v-chip x-small class="white--text blue-grey lighten-1 mr-1 tyre-chip">
                    {{ $t('device.last_frames.can_bus.tyres', { tyre: row.right.at(-1) }) }}
                  </v-chip>

                  <v-tooltip v-if="hasPressureAlarm(row.right) || hasTempAlarm(row.right)" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        v-bind="attrs"
                        v-on="on"
                        x-small
                        class="blue-grey lighten-4 data-chip"
                        :class="{ 'alarm-chip': hasPressureAlarm(row.right) || hasTempAlarm(row.right) }">
                        {{ getTyreLabel(row.right) }}
                      </v-chip>
                    </template>
                    <span>{{ getAlarmTooltip(row.right) }}</span>
                  </v-tooltip>

                  <v-chip v-else x-small class="blue-grey lighten-4 data-chip">
                    {{ getTyreLabel(row.right) }}
                  </v-chip>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- PESO + ODOMETRO -->
      <div class="d-flex flex-row my-6 justify-center align-center" v-if="getWeight !== null || getOdometer !== null">
        <div class="d-flex justify-center flex-column bottom-block">
          <span class="d-flex justify-center">
            {{ $t('device.last_frames.can_bus.trailer_weight') }}
          </span>
          <v-chip label small class="justify-center ma-1 blue-grey lighten-4 bottom-chip"> {{ getWeight }} kg </v-chip>
        </div>

        <v-divider vertical class="mx-3" />

        <div class="d-flex justify-center flex-column bottom-block">
          <span class="d-flex justify-center">
            {{ $t('device.last_frames.can_bus.odometer') }}
          </span>
          <v-chip label small class="justify-center ma-1 blue-grey lighten-4 bottom-chip"> {{ getOdometer }} km </v-chip>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  props: {
    device: {
      type: Object,
      default: () => ({}),
    },
    isPinaOn: Boolean,
    status: Object,
  },

  data() {
    return {
      timePing: Date.now(),
      timestampInterval: null,
    };
  },

  computed: {
    computedCanbusTimestamp() {
      this.timePing;
      const frame = this.device?.last_frames?.frame_CAN;
      if (!frame) return null;
      return this.getTimestampTooltip(frame.device_timestamp, new Date());
    },

    checkCANBUS() {
      return this.status?.canbus ? 'green--text' : 'gray--text';
    },

    getWeight() {
      return this.device?.last_frames?.frame_CAN?.payloadCAN?.weight ?? null;
    },

    getOdometer() {
      return this.device?.last_frames?.frame_CAN?.payloadCAN?.odometer ?? null;
    },

    getTyresRows() {
      const tyres = this.device?.last_frames?.frame_CAN?.payloadCAN?.tyres;
      if (!tyres) return [];

      const keys = Object.keys(tyres).filter((k) => tyres[k]);

      keys.sort((a, b) => {
        const numA = Number(a.match(/\d+$/)?.[0] ?? 0);
        const numB = Number(b.match(/\d+$/)?.[0] ?? 0);
        return numA - numB;
      });

      const rows = [];
      for (let i = 0; i < keys.length; i += 2) {
        rows.push({
          left: keys[i],
          right: keys[i + 1] || null,
        });
      }

      return rows;
    },
  },

  methods: {
    hasPressureAlarm(key) {
      return this.device.last_frames.frame_CAN.payloadCAN.tyres[key].alarms.press;
    },

    hasTempAlarm(key) {
      return this.device.last_frames.frame_CAN.payloadCAN.tyres[key].alarms.temp;
    },

    getTyreLabel(key) {
      const tyre = this.device.last_frames.frame_CAN.payloadCAN.tyres[key];
      const press = tyre?.steerInfo?.press;
      const temp = tyre?.steerInfo?.temp;

      if (press == null && temp == null) return '----';

      return `${press ?? '--'} Bar / ${temp ?? '--'} ºC`;
    },

    getAlarmTooltip(key) {
      const tyre = this.device.last_frames.frame_CAN.payloadCAN.tyres[key];
      const pressAlarm = tyre?.alarms?.press;
      const tempAlarm = tyre?.alarms?.temp;
      // const brakesAlarm = tyre?.alarms?.brakes; //FRENOS
      // const genericAlarm = tyre?.alarms?.generic; //GENERICA

      if (pressAlarm && tempAlarm) return this.$t('device.last_frames.can_bus.temp_pressure_alarm');
      if (pressAlarm) return this.$t('device.last_frames.can_bus.pressure_alarm');
      if (tempAlarm) return this.$t('device.last_frames.can_bus.temperature_alarm');

      return '';
    },

    getTimestampTooltip(timestampDate, actualDate) {
      const oneDay = 24 * 60 * 60 * 1000;
      const date = new Date(timestampDate);
      const diffDays = Math.round(Math.abs((actualDate - date) / oneDay));

      if (diffDays < 1) {
        const diffHours = Math.round(Math.abs((actualDate - date) / 1000 / 3600));
        if (diffHours >= 1) {
          return this.$t('dashboard.details.last_measure_hours', { hours: diffHours });
        }

        const diffMinutes = Math.round(Math.abs((actualDate - date) / 1000 / 60));
        if (diffMinutes > 1) {
          return this.$t('dashboard.details.last_measure_minutes', { minutes: diffMinutes });
        }

        return this.$t('dashboard.details.last_measure_recently');
      }

      return this.$t('dashboard.details.last_measure_in_days', { days: diffDays });
    },
  },

  mounted() {
    this.timestampInterval = setInterval(() => {
      this.timePing = Date.now();
    }, 60000);
  },

  beforeDestroy() {
    clearInterval(this.timestampInterval);
  },

  beforeUnmount() {
    clearInterval(this.timestampInterval);
  },
};
</script>

<style scoped>
.tyre-line {
  display: flex;
  align-items: center;
}

.side {
  display: flex;
  align-items: center;
  width: 48%;
}

.tyre-chip {
  min-width: 60px;
  justify-content: center;
}

.data-chip {
  min-width: 95px;
  justify-content: center;
  font-size: 11px;
}

.bottom-block {
  min-width: 120px;
}

.bottom-chip {
  min-width: 110px;
  font-size: 12px;
  justify-content: center;
}

.alarm-chip {
  background-color: #ffd6d6 !important;
  color: #b71c1c !important;
  font-weight: 700;
  border: 1.5px solid #b71c1c !important;
}
</style>
